import React, { useState, useEffect, useCallback } from 'react';
import { KeyboardArrowDown } from '@styled-icons/material/KeyboardArrowDown';
import { TextVariant } from '@naf/text';
import { Loader } from '@naf-simba/building-blocks-loaders';
import useSelector from '@redux/typedHooks';
import { MembershipBoxAccordion } from './MembershipBoxAccordion/MembershipBoxAccordion';
import { HouseholdMembershipPriceAccordion } from './HouseholdMembershipPriceAccordion/HouseholdMembershipPriceAccordion';
import { ExpandableMembershipBoxProps, ExpandableMembershipBoxVariant } from './Types';
import BlockContent from '../block-content/BlockContent';
import CTAButton from './CtaButton/CtaButton';
import { useTrackSelectItem, useTrackViewItem } from '../../hooks/useTrackProductItem';
import * as S from './Styles';

const ExpandableMembershipBoxComponent = ({
  tag,
  openByDefaultOnDesktop = false,
  showPriceInline = false,
  title,
  variant = ExpandableMembershipBoxVariant.Dew,
  terms,
  householdMembers,
  benefitAccordions = [],
  cta,
  product,
  productPrice,
  householdPrices,
  isMobile,
}: ExpandableMembershipBoxProps) => {
  const [open, setOpen] = useState(false);
  const baseUrl = useSelector((state) => state.application.baseUrl);

  useEffect(() => {
    setOpen(!isMobile && openByDefaultOnDesktop);
  }, [isMobile, openByDefaultOnDesktop]);

  const { wrapperRef } = useTrackViewItem({
    product,
    category2: 'Medlemskap',
    category3: 'Innmelding',
  });

  const { trackSelectItem } = useTrackSelectItem({
    product,
    category2: 'Medlemskap',
    category3: 'Innmelding',
  });

  const handleOpen = useCallback(() => {
    if (!open) {
      trackSelectItem();
    }

    if (isMobile || !openByDefaultOnDesktop) {
      setOpen((prevOpen) => !prevOpen);
    }
  }, [open, trackSelectItem, isMobile, openByDefaultOnDesktop]);

  return (
    <S.Wrapper ref={wrapperRef} $variant={variant}>
      {tag && <S.StyledTag text="Mest populær" color="secondary" />}
      <S.HeaderContent
        onClick={handleOpen}
        $showPriceInline={showPriceInline}
        $isClickable={isMobile || !openByDefaultOnDesktop}
      >
        {title && (
          <S.StyledHeader variant={TextVariant.Header3} $showPriceInline={showPriceInline}>
            {title}
          </S.StyledHeader>
        )}
        {!open && (
          <S.ClosedPriceWrapper $showPriceInline={showPriceInline}>
            {productPrice ? (
              <S.Price variant={TextVariant.Header3}>
                {productPrice} kr
                {!showPriceInline && ' for 12 måneder'}
              </S.Price>
            ) : (
              <S.LoaderWrapper>
                <Loader size="small" />
                <S.LoadingPrice variant={TextVariant.Header3}>Laster pris..</S.LoadingPrice>
              </S.LoaderWrapper>
            )}
          </S.ClosedPriceWrapper>
        )}
      </S.HeaderContent>
      {open && (
        <S.Content>
          {benefitAccordions?.map((item) => (
            <MembershipBoxAccordion
              key={item?.title}
              variant={item?.variant}
              title={item?.title}
              content={item?.content}
              onExpand={trackSelectItem}
            />
          ))}
          <S.PriceWrapper>
            {productPrice ? (
              <S.Price variant={TextVariant.Header3}>{productPrice} kr for 12 måneder</S.Price>
            ) : (
              <S.LoaderWrapper>
                <Loader size="small" />
                <S.LoadingPrice variant={TextVariant.Header3}>Laster pris..</S.LoadingPrice>
              </S.LoaderWrapper>
            )}
          </S.PriceWrapper>
          {householdMembers && terms && (
            <HouseholdMembershipPriceAccordion
              title="Medlemskap for flere personer"
              prices={householdPrices}
              terms={terms}
            />
          )}
          {cta && (
            <S.ButtonWrapper>
              <CTAButton cta={cta} baseUrl={baseUrl} />
            </S.ButtonWrapper>
          )}
          {!householdMembers && terms && (
            <S.TermsWrapper>
              <S.Terms variant={TextVariant.Header3}>Betingelser</S.Terms>
              <BlockContent value={terms} />
            </S.TermsWrapper>
          )}
        </S.Content>
      )}
      <S.Chevron onClick={handleOpen} $isOpen={open} $isOpenByDefault={openByDefaultOnDesktop}>
        <KeyboardArrowDown size={24} />
      </S.Chevron>
    </S.Wrapper>
  );
};

export const ExpandableMembershipBox = React.memo(ExpandableMembershipBoxComponent);
